// ===== Interfaces =====

import {
    ICartItem,
    IPartialUserItem,
}                       from '../interfaces';
import { logAuthDebug } from './authDebug';

type AuthResultCache = {
  user?: any;
  accessToken?: string;
  providerId?: string | null;
};

type AppState = {
    nextPath?: string;
    scrollY?: number;
    draft?: Record<string, any>,
    hasSound?: boolean,
    signingInOut?: boolean,
    oldUserData?: {
        user: IPartialUserItem | undefined,
        email: string | undefined,
        userSessions: string[] | undefined,
        userUpdateTimestamps: number[] | undefined,
        oldUserCart?: ICartItem[],
    },
    auth?: AuthResultCache,
};

const KEY = 'authRedirectState:v1';

export function saveStateForRedirect(state: AppState): void {
    // Prefer sessionStorage if available; else fall back to window.name
    try {
        sessionStorage.setItem(KEY, JSON.stringify(state));
        logAuthDebug('state-handoff-saved', {
            storage: 'sessionStorage',
            hasAuth: !!state.auth,
            hasOldUserData: !!state.oldUserData,
        });
    } catch {
        const existing = safeParseWindowName();
        (window as any).name = JSON.stringify({ ...existing, [KEY]: state });
        logAuthDebug('state-handoff-saved', {
            storage: 'window.name',
            hasAuth: !!state.auth,
            hasOldUserData: !!state.oldUserData,
        });
    }
}

export function restoreStateAfterRedirect(): AppState | null {
    // Try sessionStorage first
    try {
        const raw = sessionStorage.getItem(KEY);
        if (raw) {
            sessionStorage.removeItem(KEY);
            const parsed = JSON.parse(raw) as AppState;
            logAuthDebug('state-handoff-restored', {
                storage: 'sessionStorage',
                hasAuth: !!parsed?.auth,
                hasOldUserData: !!parsed?.oldUserData,
            });
            return parsed;
        }
    } catch { /* ignore */ }

    // Fallback: window.name
    const obj = safeParseWindowName();
    const found: AppState | null = obj?.[KEY] ?? null;
    if (found) {
        delete obj[KEY];
        (window as any).name = JSON.stringify(obj);
        logAuthDebug('state-handoff-restored', {
            storage: 'window.name',
            hasAuth: !!found?.auth,
            hasOldUserData: !!found?.oldUserData,
        });
    }
    if (!found) {
        logAuthDebug('state-handoff-missing');
    }
    return found;
}

function safeParseWindowName(): any {
    try {
        return JSON.parse((window as any).name || '{}');
    } catch { return {}; }
}
